<template>
  <div class="container-fluid pt-2">
    <div v-if="pageHasLoaded">
      <!-- Top Row -->
      <div class="row">
        <div class="col-md-4 col-sm-12">
          <div class="text-left py-3">
            <h3 class="text-weight-900">Inventory List</h3>
          </div>
        </div>

        <div class="col-md-8 col-sm-12">
          <div class="text-center py-3">
            <div v-if="isInventoryManager" class="d-flex justify-content-end">
              <div>
                <a title="Record Purchases" href="/bulk-restock" class="btn btn-sm btn-outline-primary mx-1">Bulk Restock</a>
                <p><small>Record Purchases</small></p>
              </div>

              <div>
                <a href="/addprod" class="btn btn-sm btn-outline-primary mx-1">Create an Item</a>
              </div>

              <div class="dropdown">
                <button class="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" data-toggle="dropdown" data-offset="-100,3">
                  More...
                </button>
                <div class="dropdown-menu">
                  <a v-on:click="ImportInventoryModal" class="dropdown-item">Import Items</a>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item" href="/purchases">Purchases</a>
                  <div class="dropdown-divider"></div>
                  <a href="/group-inventory" class="dropdown-item">Grouped Inventory</a>
                  <div class="dropdown-divider"></div>
                  <b-button class="dropdown-item" variant="outline-secondary" v-b-modal.modal-4>View Categories</b-button>
                  <div class="dropdown-divider"></div>
                  <button class="dropdown-item" @click="createProductCatModal">Create New Category</button>
                  <div class="dropdown-divider"></div>
                  <a href="/inventory-activity" class="dropdown-item">View Inventory Activity</a>
                  <div class="dropdown-divider"></div>
                  <a href="/inventory?low-stock=true" class="dropdown-item">Low Stock</a>
                  <div class="dropdown-divider"></div>
                  <a href="/stock-take" class="dropdown-item">Do Stock Take</a>
                  <div class="dropdown-divider"></div>
                  <a href="/inventory-variance-report" class="dropdown-item">Inventory Variance</a>
                  <div class="dropdown-divider"></div>
                  <button class="dropdown-item" @click="downloadInventory">Download Inventory <i class="bi bi-download"></i></button>
                </div>
              </div>
            </div>

            <div v-else class="d-flex justify-content-end">
              <a title="Do stock take" href="/stock-take" class="btn btn-sm btn-outline-primary mx-1">Do Stock Take</a>
            </div>
          </div>
        </div>
      </div>

      <!-- Metrics -->
      <div class="row" v-if="isInventoryManager">
        <div class="col-6 col-md-3 border-right">
          <div class="border-bottom border-primary text-center bg-success-light" style="width:75%; margin:0 auto;">
            <p class="text-bold">{{ uniqueStockCount }}</p><small>Items</small>
          </div>
        </div>
        <div class="col-6 col-md-3 border-right">
          <div class="border-bottom border-primary text-center bg-success-light" style="width:75%; margin:0 auto;">
            <p class="text-bold">{{ stockCount }}</p><small>Total Stock Count</small>
          </div>
        </div>
        <div class="col-6 col-md-3 border-right">
          <div class="border-bottom border-primary text-center bg-danger-light" style="width:75%; margin:0 auto;">
            <p class="text-bold">{{ estimatedSalesValue }}</p><small>Estimated Sales</small>
          </div>
        </div>
        <div class="col-6 col-md-3 border-right">
          <div class="border-bottom border-primary text-center bg-danger-light" style="width:75%; margin:0 auto;">
            <p class="text-bold">{{ stockValue }}</p><small>Total Stock Value</small>
          </div>
        </div>
      </div>

      <!-- Search & Sort -->
      <div class="row py-1">
        <div class="col-md-6 col-sm-12 py-1">
          <form @submit.prevent="searchQueryItems">
            <div class="input-group w-100 my-1">
              <input type="text" class="form-control" v-model="searchItems" placeholder="Search Items" />
              <div class="input-group-append">
                <button class="btn btn-outline-secondary" type="submit"><i class="fa fa-search"></i></button>
              </div>
            </div>
          </form>
        </div>

        <div class="col-md-6 col-sm-12 py-1">
          <b-form inline>
            <label class="mr-sm-2 p">Sort by:</label>
            <b-form-select v-model="queryselected" :options="itemCategories" class="mb-2 mr-sm-2 mb-sm-0 w-75">
              <b-form-select-option value="null">-- Most Relevant --</b-form-select-option>
            </b-form-select>
          </b-form>
        </div>
      </div>

      <!-- Bulk action bar -->
      <div v-if="isInventoryManager && selectedCount > 0" class="mb-2">
        <div class="d-flex justify-content-between align-items-center p-2 bg-light rounded">
          <div>
            <strong>{{ selectedCount }}</strong> item(s) selected
            <button class="btn btn-link btn-sm" @click="clearSelection">Clear</button>
          </div>

          <div>
            <div class="btn-group">
              <button type="button" class="btn btn-outline-secondary btn-sm dropdown-toggle" data-toggle="dropdown">
                Bulk Actions
              </button>
              <div class="dropdown-menu dropdown-menu-right">
                <a class="dropdown-item" href="#" @click.prevent="openBulkTransferEditor">Bulk Transfer</a>
                <a class="dropdown-item" href="#" @click.prevent="openIssueModal">Issue Items</a>
                <a class="dropdown-item" href="#" @click.prevent="confirmBulkDelete">Bulk Delete</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div style="margin-bottom: 5em;">
        <b-table
          id="inventory-table"
          :items="filteredInventoryItems"
          :fields="tableFields"
          striped
          hover
          small
          sticky-header="100vh"
          sort-icon-left
          responsive="sm"
          :current-page="currentPageInventoryTable"
          :per-page="perPageInventoryTable"
          @row-clicked="handleRowClicked"
        >
          <!-- header selection column -->
          <template #head(selected)="data">
            <input type="checkbox" :checked="selectAll" @change="toggleSelectAll" />
          </template>

          <!-- cell selection -->
          <template #cell(selected)="row">
            <input
              type="checkbox"
              :checked="isSelected(row.item.id)"
              @click.stop="toggleSelect(row.item.id)"
            />
          </template>

          <!-- product -->
          <template #cell(product)="data">
            <p class="text-primary underline-primary">{{ data.item.product }}</p>
          </template>

          <!-- stock_value -->
          <template #cell(stock_value)="data">
            <p v-if="isInventoryManager" class="text-primary underline-primary">
              {{ convertCurrencyToLocal(data.item.wholesale_ppu * data.item.remaining_units) }}
            </p>
            <p v-else class="text-danger underline-primary"> -</p>
          </template>

          <!-- ppu -->
          <template #cell(ppu)="data">
            <p>{{ convertCurrencyToLocal(data.item.ppu) }}</p>
          </template>

          <!-- wholesale_sp -->
          <template #cell(wholesale_sp)="data">
            <p v-if="companySettings.has_wholesale">{{ convertCurrencyToLocal(data.item.wholesale_sp) }}</p>
            <p v-else>-</p>
          </template>

          <!-- wholesale_ppu -->
          <template #cell(wholesale_ppu)="data">
            <p v-if="isInventoryManager" class="text-primary underline-primary">{{ convertCurrencyToLocal(data.item.wholesale_ppu) }}</p>
            <p v-else class="text-danger underline-primary"> -</p>
          </template>

          <!-- remaining_units -->
          <template #cell(remaining_units)="data">
            <p v-if="isInventoryManager" class="text-primary underline-primary">{{ data.item.remaining_units }}</p>
            <p v-else class="text-danger underline-primary"> -</p>
          </template>
        </b-table>

        <b-pagination v-model="currentPageInventoryTable" :total-rows="rowsCompute" :per-page="perPageInventoryTable" aria-controls="inventory-table"></b-pagination>
      </div>

      <!-- ===========================
           ORIGINAL MODALS (preserved)
           =========================== -->

      <!-- Product Modal -->
      <b-modal ref="productmodal" id="modal-center" hide-footer static>
        <div v-if="selectedProduct.is_service">
          <h5 class="text-center py-2">Service Details</h5>
          <table class="table table-striped">
            <tbody>
              <tr><td>Service:</td><td>{{ selectedProduct.product }}</td></tr>
              <tr><td>Service Rate:</td><td>{{ convertCurrencyToLocal(selectedProduct.ppu) }}</td></tr>
              <tr><td>Date Updated:</td><td>{{ formatDate(selectedProduct,'hh:mm a DD-MM-Y') }}</td></tr>
            </tbody>
          </table>

          <div class="float-left">
            <div class="btn-group">
              <button v-if="isInventoryManager" class="btn btn-outline-danger rounded mx-1 my-1 btn-sm" @click="DeleteProd(selectedProduct.id)">Delete</button>
            </div>
            <button v-if="isInventoryManager" class="btn btn-outline-dark rounded mx-1 my-1 btn-sm" @click="editProdModal(selectedProduct.id)">Edit</button>
          </div>
        </div>

        <div v-else>
          <div class="text-center py-2">
            <h5>Product Details</h5>
            <hr />
            <p class="py-2">Estimated Profits: <span class="text-primary">{{convertCurrencyToLocal((selectedProduct.ppu - selectedProduct.wholesale_ppu))}}</span></p>
            <p>Percentage Margin: <span class="text-primary">{{ percentageMargins }}%</span></p>
          </div>

          <table class="table table-striped">
            <tbody>
              <tr><td>Item:</td><td>{{ selectedProduct.product }}</td></tr>
              <tr><td>Retail:</td><td>{{ convertCurrencyToLocal(selectedProduct.ppu) }}</td></tr>
              <tr v-if="companySettings.has_wholesale"><td>Wholesale:</td><td>{{ convertCurrencyToLocal(selectedProduct.wholesale_sp) }}</td></tr>
              <tr v-if="companySettings.expiry_date"><td>Expiry Date:</td><td>{{ convertCurrencyToLocal(selectedProduct.expiry_date) }}</td></tr>
              <tr><td>Stock:</td><td>{{ selectedProduct.remaining_units }} {{selectedProduct.measuring_unit}}</td></tr>
              <tr><td>Buying Price:</td><td>{{ convertCurrencyToLocal(selectedProduct.wholesale_ppu) }}</td></tr>
              <tr><td>Date Updated:</td><td>{{ formatDate(selectedProduct.updated_at,'hh:mm a DD-MM-Y') }}</td></tr>
            </tbody>
          </table>

          <div class="row">
            <div class="col-sm-6 col-md-6 col-6">
              <a class="btn btn-block btn-outline-primary" href="#" @click="editProdModal(selectedProduct.id)">Edit</a>
            </div>
            <div class="col-sm-6 col-md-6 col-6">
              <div class="dropdown">
                <a class="btn btn-outline-secondary btn-block dropdown-toggle" v-if="isInventoryManager" data-toggle="dropdown">More ...</a>
                <div class="dropdown-menu">
                  <a class="dropdown-item" href="#" @click="editProdModal(selectedProduct.id)">Edit</a>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item" href="#" @click="transferProdModal(selectedProduct)">Transfer Item</a>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item" href="#" @click="restockProdModal(selectedProduct.id)">Restock</a>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item" href="#" @click="openIssueModal(selectedProduct.id)">Issue Stock</a>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item" :href="'/inventory-activity?p-identifier-id=' + selectedProduct.id">View Item Movement</a>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item" :href="'/product-customer-insight?id=' + selectedProduct.id">View Customer Insights</a>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item" :href="'/inventory-activity?only-sales=1&p-identifier-id=' + selectedProduct.id + '&start-date=&end-date='">View Item Sales</a>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item" href="#" @click="restockLogsModalOpen(selectedProduct)">View Restock Logs</a>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item" href="#" @click="linkProductsModal()">Link Product to another</a>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item text-danger" href="#" @click="DeleteProd(selectedProduct.id)">Delete</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-modal>

      <!-- Product restock modal (single) -->
      <b-modal ref="productmodal_restock" id="modal-center" hide-footer static>
        <div class="text-center">
          <img :src="selectedProduct.img" />
          <h2 class="text-weight-500">Restock {{ selectedProduct.product }}</h2>
          <hr />
          <p>Remaining - {{ selectedProduct.remaining_units }} {{ selectedProduct.measuring_unit }}</p>
        </div>

        <form @submit.prevent="onSubmitRestock" v-if="show">
          <input id="input-1" placeholder="New quantities e.g 100" v-model="restockform.quantity" type="text" class="form-control py-1" required />
          <p class="text-left"><small>Note this will be added to existing stock</small></p>

          <div class="custom-control custom-switch my-2">
            <input v-model="restockform.adjustBuyingPrice" type="checkbox" class="custom-control-input" id="customSwitch1">
            <label class="custom-control-label" for="customSwitch1"><small>Adjust Buying Price</small></label>
          </div>

          <div v-if="restockform.adjustBuyingPrice">
            <input placeholder="New Buying Price" v-model="restockform.newBuyingPrice" type="text" class="form-control py-1" required />
            <div class="form-check">
              <input class="form-check-input" type="checkbox" v-model="restockform.changeBuyingPriceImmediately" id="defaultCheck1">
              <label class="form-check-label" for="defaultCheck1">
                <small>Apply this new buying price immediately</small>
              </label>
            </div>
            <p><small class="text-danger">Leaving this will update buying price after old stock is sold out</small></p>
          </div>

          <hr/>
          <p>Supplier details</p>
          <small>To add or search supplier just type the name of supplier</small>

          <div v-if="supplierDetails.selectedSupplier.supplier">
            <p class="text-capitalize"><span class="text-bold">Supplier: </span>{{ supplierDetails.selectedSupplier.supplier }}. <a @click="resetSupplier" href="#">Change</a></p>
          </div>

          <div class="form-group" v-else>
            <input class="form-control" v-model="supplierDetails.searchTerm" placeholder="Supplier name">
            <div v-if="supplierDetails.searchTable">
              <div v-if="supplierDetails.filteredSuppliers.length > 0">
                <table class="table table-striped table-sm">
                  <thead><th>Name</th><th>Phone</th><th>Email</th></thead>
                  <tbody>
                    <tr class="text-dark text-capitalize underline text-sm" v-for="supplier in supplierDetails.filteredSuppliers" :key="supplier.id" @click="rowSelectSupplier(supplier)">
                      <td class="text-primary underline-primary">{{ supplier.supplier || '' }}</td>
                      <td>{{ supplier.phone || '' }}</td>
                      <td>{{ supplier.email || '' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else>
                <a @click="openAddSupplierModal" href="#">Create supplier - {{ supplierDetails.searchTerm }}</a>
              </div>
            </div>
          </div>

          <input id="input-1" v-model="restockform.product_id" type="hidden" value="selectedProduct.id" />
          <hr />
          <button type="submit" class="btn btn-block btn-sm btn-primary mb-2">Submit New Stock</button>
        </form>
      </b-modal>

      <!-- Restock Logs modal -->
      <b-modal size="xl" ref="restocklogsmodal" id="modal-center" hide-footer static>
        <div class="text-center mb-3">
          <h5 class="font-weight-bold">Restock Logs for {{ selectedProduct?.product }}</h5>
        </div>

        <div class="row mb-3">
          <div class="col-md-4">
            <label>Date Range</label>
            <date-range-picker v-model="filters.dateRange" :auto-apply="autoApply" :ranges="defaultRanges" class="custom-width-datepicker" @finish-selection="fetchRestockLogs" />
          </div>

          <div class="col-md-4">
            <label>Supplier</label>
            <v-select v-model="filters.supplier_id" :options="supplierOptions" label="name" placeholder="Search Supplier..." @input="fetchRestockLogs" @change="fetchRestockLogs" :clearable="true" />
          </div>

          <div class="col-md-4">
            <label>Price Change</label>
            <select v-model="filters.buying_price_change" @change="fetchRestockLogs" class="form-control">
              <option value="">All</option>
              <option value="1">Changed</option>
              <option value="0">No Change</option>
            </select>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th>#</th><th>Opening Stock</th><th>Restocked Qty</th><th>Closing Stock</th><th>Old Buying Price</th><th>New Buying Price</th><th>Supplier</th><th>Updated By</th><th>Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(log, index) in restockLogs" :key="log.id">
                <td>{{ index + 1 }}</td>
                <td>{{ log.old_quantity }}</td>
                <td>{{ log.new_quantity }}</td>
                <td>{{ log.old_quantity + log.new_quantity }}</td>
                <td>{{ convertCurrencyToLocal(log.old_buying_price) }}</td>
                <td>{{ convertCurrencyToLocal(log.new_buying_price) }}</td>
                <td>{{ log.supplier?.supplier || 'N/A' }}</td>
                <td>{{ log.user?.name || 'System' }}</td>
                <td>{{ formatDate(log.created_at, 'DD-MM-YYYY hh:mm A') }}</td>
              </tr>
              <tr v-if="restockLogs.length === 0">
                <td colspan="9" class="text-center text-muted">No restocking logs found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-modal>

      <!-- Link Product Modal (restored) -->
      <b-modal size="md" ref="linkProductModal" id="modal-center" hide-footer static>
        <div class="text-center"><h5>Link Items</h5></div>
        <form @submit.prevent="submitLinkProduct">
          <label>Items to link</label>

          <div v-if="linkedProductForm.hideSearchInput">
            <p v-if="linkedProductForm.searchTerm"> <b>Primary product </b>{{ selectedProduct.product }}</p>
            <hr class="my-2" />
            <p v-if="linkedProductForm.searchTerm"> <b>Target product </b>{{ linkedProductForm.target_product }}</p>
          </div>

          <div class="form-group" v-if="!linkedProductForm.hideSearchInput">
            <p><small>Enter name of product</small></p>
            <input class="form-control" v-model="linkedProductForm.searchTerm" placeholder="Item name, type...." />
            <div v-if="linkedProductForm.searchTable">
              <div v-if="linkedProductForm.filteredInventoryItems.length > 0">
                <table class="table table-striped table-sm">
                  <thead><th>Item</th></thead>
                  <tbody>
                    <tr class="text-dark text-capitalize underline text-sm" v-for="item in linkedProductForm.filteredInventoryItems" :key="item.id" @click="linkProductSelected(item)">
                      <td class="text-primary text-left underline-primary" style="font-size:11px;">{{ item.product || '' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="form-group pt-2">
            <label>Quantity to change</label>
            <p><small>When 1 of the primary item is deducted how many target item should be affected</small></p>
            <input class="form-control" v-model="linkedProductForm.quantity_change" required />
          </div>

          <div class="form-group">
            <button class="btn btn-primary">Submit</button>
          </div>
        </form>
      </b-modal>

      <!-- Edit product modal (preserved) -->
      <b-modal size="lg" ref="editproductmodal" id="bv-modal-edit" hide-footer>
        <div v-if="selectedProduct.is_service">
          <div class="text-center">
            <h2 class="text-weight-700 text-primary text-center">Edit {{ selectedProduct.product }}</h2>
            <hr />
          </div>

          <form @submit.prevent="updateProduct">
            <div id="productAccordion" class="accordion">
              <!-- Basic Info card (service) -->
              <div class="card">
                <div class="card-header">
                  <h2 class="mb-0">
                    <button class="btn btn-link" type="button" data-toggle="collapse" data-target="#collapseBasic" aria-expanded="true">Basic Info</button>
                  </h2>
                </div>
                <div id="collapseBasic" class="collapse show" data-parent="#productAccordion">
                  <div class="card-body">
                    <div class="form-group">
                      <label>Service Title</label>
                      <input class="border form-control" :placeholder="selectedProduct.product" v-model="editprodform.product" />
                    </div>

                    <div class="form-group">
                      <label>Adjust Service Rate</label>
                      <input class="border form-control" :placeholder="selectedProduct.ppu" v-model="editprodform.ppu" />
                    </div>

                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" v-model="editprodform.is_service" id="isService">
                      <label class="form-check-label" v-if="!editprodform.is_service" for="isService">Change this Item to a service</label>
                      <label class="form-check-label" v-else for="isService">Change this service to item</label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card" v-if="companySettings.has_reservations">
                <div class="card-header">
                  <h2 class="mb-0">
                    <button class="btn btn-link collapsed" type="button" data-toggle="collapse" data-target="#collapseVariations" aria-expanded="false">Item Variations</button>
                  </h2>
                </div>
                <div id="collapseVariations" class="collapse" data-parent="#productAccordion">
                  <div class="card-body">
                    <div v-for="(variation, index) in editprodform.variations" :key="variation.id || index" class="border rounded p-3 mb-3">
                      <div class="form-row">
                        <div class="form-group col-md-4"><label>Name</label><input v-model="variation.name" class="form-control" /></div>
                        <div class="form-group col-md-4"><label>Code</label><input v-model="variation.code" class="form-control" /></div>
                        <div class="form-group col-md-4"><label>Board Type</label>
                          <select v-model="variation.board_type" class="form-control">
                            <option value="full_board">Full Board</option>
                            <option value="half_board">Half Board</option>
                            <option value="bed_breakfast">Bed & Breakfast</option>
                            <option value="room_only">Room Only</option>
                            <option value="all_inclusive">All Inclusive</option>
                          </select>
                        </div>
                      </div>

                      <div class="form-row">
                        <div class="form-group col-md-4"><label>Price / Night</label><input type="number" step="any" min="0" v-model="variation.price_per_night" class="form-control" /></div>
                        <div class="form-group col-md-4"><label>Price / Adult</label><input type="number" step="any" min="0" v-model="variation.price_per_adult" class="form-control" /></div>
                        <div class="form-group col-md-4"><label>Price / Child</label><input type="number" step="any" min="0" v-model="variation.price_per_child" class="form-control" /></div>
                      </div>

                      <div class="form-group form-check">
                        <input type="checkbox" class="form-check-input" v-model="variation.is_active" />
                        <label class="form-check-label">Active</label>
                      </div>

                      <button type="button" class="btn btn-outline-primary btn-sm" @click="removeVariation(index)">Remove Variation</button>
                    </div>

                    <button type="button" class="btn btn-outline-secondary btn-sm" @click="addVariation">+ Add Variation</button>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group text-center py-2">
              <button type="submit" class="btn btn-primary">Submit Service Details</button>
            </div>
          </form>
        </div>

        <div v-else>
          <!-- Edit product (non-service) structure preserved -->
          <div class="text-center">
            <h2 class="text-weight-700 text-primary text-center">Edit {{ selectedProduct.product }}</h2>
            <hr />
            <p class="py-2">Estimated Profits: <span class="text-primary">{{convertCurrencyToLocal((selectedProduct.ppu - selectedProduct.wholesale_ppu)) }}</span></p>
            <p>Percentage Margin:  <span class="text-primary">{{ percentageMargins }}%</span></p>
          </div>

          <form @submit.prevent="updateProduct">
            <div id="productAccordion" class="accordion">

              <div class="card">
                <div class="card-header">
                  <h2 class="mb-0">
                    <button class="btn btn-link" type="button" data-toggle="collapse" data-target="#collapseBasic" aria-expanded="true">Basic Info</button>
                  </h2>
                </div>
                <div id="collapseBasic" class="collapse show" data-parent="#productAccordion">
                  <div class="card-body">
                    <div class="form-group"><label>Item Title</label><input class="form-control" v-model="editprodform.product" :placeholder="selectedProduct.product" /></div>
                    <div class="form-group"><label>Category</label>
                      <select class="form-control" v-model="editprodform.productcat_id">
                        <option :value="null">Select category</option>
                        <option v-for="category in itemCategories" :key="category.value" :value="category.value">{{ category.text }}</option>
                      </select>
                    </div>
                    <div class="form-group"><label>Selling Price</label><input type="number" step="any" min="0" class="form-control" v-model="editprodform.ppu" :placeholder="selectedProduct.ppu" /></div>
                    <div class="form-group"><label>Buying Price</label><input type="number" step="any" min="0" class="form-control" v-model="editprodform.wholesale_ppu" :placeholder="selectedProduct.wholesale_ppu" /></div>
                  </div>
                </div>
              </div>

              <div class="card">
                <div class="card-header"><h2 class="mb-0"><button class="btn btn-link collapsed" type="button" data-toggle="collapse" data-target="#collapseStock">Stock Management</button></h2></div>
                <div id="collapseStock" class="collapse" data-parent="#productAccordion">
                  <div class="card-body">
                    <div class="form-group"><label>Remaining Units</label><input class="form-control" v-model="editprodform.remaining_units" :placeholder="selectedProduct.remaining_units" /><small class="form-text text-muted">Adjust the number of items in stock</small></div>
                    <div class="form-group"><label>Measuring Unit</label><input class="form-control" v-model="editprodform.measuring_unit" :placeholder="selectedProduct.measuring_unit" /></div>
                    <div class="form-group"><label>Minimum Stock</label><input type="number" class="form-control" v-model="editprodform.minimum_stock" :placeholder="selectedProduct.minimum_stock" /></div>
                    <div class="form-group"><label>Low Stock Threshold</label><input type="number" class="form-control" v-model="editprodform.low_stock" :placeholder="selectedProduct.low_stock" /></div>
                  </div>
                </div>
              </div>

              <div class="card" v-if="companySettings.has_wholesale || companySettings.expiry_date">
                <div class="card-header"><h2 class="mb-0"><button class="btn btn-link collapsed" type="button" data-toggle="collapse" data-target="#collapseWholesale">Wholesale & Expiry</button></h2></div>
                <div id="collapseWholesale" class="collapse" data-parent="#productAccordion">
                  <div class="card-body">
                    <div class="form-group" v-if="companySettings.has_wholesale"><label>Wholesale Selling Price</label><input type="number" step="any" min="0" class="form-control" v-model="editprodform.wholesale_sp" :placeholder="selectedProduct.wholesale_sp" /></div>
                    <div class="form-group" v-if="companySettings.expiry_date"><label>Expiry Date</label><input type="date" class="form-control" v-model="editprodform.expiry_date" :placeholder="selectedProduct.expiry_date" /></div>
                  </div>
                </div>
              </div>

              <div class="card">
                <div class="card-header"><h2 class="mb-0"><button class="btn btn-link collapsed" type="button" data-toggle="collapse" data-target="#collapseSalesSettings">Sales <span v-if="companySettings.has_reservations">& Reservation Settings</span></button></h2></div>
                <div id="collapseSalesSettings" class="collapse" data-parent="#productAccordion">
                  <div class="card-body">
                    <div class="form-check"><input class="form-check-input" type="checkbox" v-model="editprodform.can_be_sold" id="canBeSold"><label class="form-check-label" for="canBeSold">Item can be sold</label></div>
                    <div class="form-check" v-if="companySettings.has_reservations"><input class="form-check-input" type="checkbox" v-model="editprodform.is_reservable" id="isReservable"><label class="form-check-label" for="isReservable">Item is reservable</label></div>
                    <div class="form-check"><input class="form-check-input" type="checkbox" v-model="editprodform.is_service" id="isService"><label class="form-check-label" v-if="!editprodform.is_service" for="isService">Change this Item to a service</label><label class="form-check-label" v-else for="isService">Change this service to item</label></div>
                    <div class="form-check"><input class="form-check-input" type="checkbox" v-model="editprodform.is_directly_proportional" id="isDirectlyProportional"><label class="form-check-label" for="isDirectlyProportional">Price directly proportional to quantity</label></div>
                  </div>
                </div>
              </div>

              <div class="card">
                <div class="card-header"><h2 class="mb-0"><button class="btn btn-link collapsed" type="button" data-toggle="collapse" data-target="#collapseMoreDetails">More Details</button></h2></div>
                <div id="collapseMoreDetails" class="collapse" data-parent="#productAccordion">
                  <div class="card-body">
                    <div class="form-group"><label>Discount (%)</label><input type="number" class="form-control" v-model="editprodform.discount" placeholder="e.g. 10" /></div>
                    <div class="form-group"><label>Commission (%)</label><input type="number" class="form-control" v-model="editprodform.commission" placeholder="e.g. 5" /></div>
                    <div class="form-group"><label>Weight</label><input class="form-control" v-model="editprodform.weight" placeholder="e.g. 500g" /></div>
                    <div class="form-group"><label>Color</label><input class="form-control" v-model="editprodform.bg_color" placeholder="e.g. Red" /></div>
                    <div class="form-group"><label>Tags (comma separated)</label><input class="form-control" v-model="editprodform.tags" placeholder="e.g. fragile, perishable" /></div>
                    <div class="form-group"><label>Store Keeping Unit (SKU)</label><input class="form-control" v-model="editprodform.sku" placeholder="e.g. SKU-00123" /></div>
                  </div>
                </div>
              </div>

            </div>

            <div class="form-group text-center py-3">
              <button type="submit" class="btn btn-primary">Submit Changes</button>
            </div>
          </form>
        </div>
      </b-modal>

      <!-- Create Product Category -->
      <b-modal ref="createProductCatModal" id="bv-modal-edit" hide-footer>
        <h6 class="py-2">Create New Product Category</h6>
        <form @submit.prevent="createNewCategory">
          <div class="form-group"><input class="form-control border" placeholder="Product Category" v-model="newproductcat" /></div>
          <div class="form-group"><button type="submit" class="btn btn-primary">Submit</button></div>
        </form>
      </b-modal>

      <!-- Item column select -->
       <b-modal ref="itemcolumnselect" size="lg" id="bv-modal-edit" no-close-on-backdrop hide-footer>
                <h5 class="py-3 text-center">Select columns to import that matches the headers from the file you uploaded
                </h5>
                <p><small class="text-danger">* Required</small></p>
                <hr />
                <div class="row">
                    <div class="col-md-6 col-sm-12">
                        <p> Item Name <small class="text-danger">*</small> </p>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <select class="custom-select" v-model="itemColumnImportOptions.itemName">
                            <option value="null">Choose matching column</option>
                            <option :value="importhead" v-for="importhead in importInventoryHeaders">{{ importhead }}</option>
                        </select>
                        <small class="text-danger" v-if="importSelectionErrors.itemName">{{ importSelectionErrors.itemName }}</small>
                    </div>
                </div>
                <hr class="my-2" />
                <div class="row">
                    <div class="col-md-6 col-sm-12">

                        <p> Category <small class="text-danger">*</small></p>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <select class="custom-select" v-model="itemColumnImportOptions.category">
                            <option value="null">Choose matching column</option>
                            <option :value="importhead" v-for="importhead in importInventoryHeaders">{{ importhead }}</option>
                        </select>
                        <small class="text-danger" v-if="importSelectionErrors.category">{{ importSelectionErrors.category }}</small>
                    </div>
                </div>
                <hr class="my-2" />
                <div class="row">
                    <div class="col-md-6 col-sm-12">
                        <p> SKU <small>(Store keeping unit)</small> </p>
                    </div>
                    <div class="col-md-6 col-sm-12">

                        <select class="custom-select" v-model="itemColumnImportOptions.sku">
                            <option value="null">Choose matching column</option>
                            <option :value="importhead" v-for="importhead in importInventoryHeaders">{{ importhead }}</option>
                        </select>

                    </div>
                </div>
                <hr class="my-2" />
                <div class="row">
                    <div class="col-md-6 col-sm-12">

                        <p> Buying Price <small class="text-danger">*</small> </p>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <select class="custom-select" v-model="itemColumnImportOptions.buyingPrice">
                            <option value="null">Choose matching column</option>
                            <option :value="importhead" v-for="importhead in importInventoryHeaders">{{ importhead }}</option>
                        </select>
                        <small class="text-danger" v-if="importSelectionErrors.buyingPrice">{{ importSelectionErrors.buyingPrice }}</small>
                    </div>
                </div>
                <hr class="my-2" />
                <div class="row">
                    <div class="col-md-6 col-sm-12">

                        <p> Selling Price <small class="text-danger">*</small></p>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <select class="custom-select" v-model="itemColumnImportOptions.sellingPrice">
                            <option value="null">Choose matching column</option>
                            <option :value="importhead" v-for="importhead in importInventoryHeaders">{{ importhead }}</option>
                        </select>
                        <small class="text-danger" v-if="importSelectionErrors.sellingPrice">{{ importSelectionErrors.sellingPrice }}</small>
                    </div>
                </div>
                <hr class="my-2" />
                <div class="row">
                    <div class="col-md-6 col-sm-12">

                        <p> Remaining quantities <small class="text-danger">*</small></p>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <select class="custom-select" v-model="itemColumnImportOptions.remainingUnits">
                            <option value="null">Choose matching column</option>
                            <option :value="importhead" v-for="importhead in importInventoryHeaders">{{ importhead }}</option>
                        </select>
                    </div>
                </div>
                <hr class="my-2" />
                <div class="row">
                    <div class="col-md-6 col-sm-12">

                        <p> Wholesale selling price </p>
                        <small class="text-secondary py-1">Only applies if you do wholesale</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <select class="custom-select" v-model="itemColumnImportOptions.wholesaleSellingPrice">
                            <option value="null">Choose matching column</option>
                            <option :value="importhead" v-for="importhead in importInventoryHeaders">{{ importhead }}</option>
                        </select>
                    </div>
                </div>
                <hr class="my-2" />
                <div class="row">
                    <div class="col-md-6 col-sm-12">
                        <p> Measuring Units <small class="text-danger">*</small></p>
                        <small class="text-secondary py-1">Kg, Pieces, Units, Litres etc</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <select class="custom-select" v-model="itemColumnImportOptions.measuringUnit">
                            <option value="null">Choose matching column</option>
                            <option :value="importhead" v-for="importhead in importInventoryHeaders">{{ importhead }}</option>
                        </select>
                    </div>

                </div>
                <div class="row">
                    <div class="col-md-6 col-sm-12">
                        <p> Low Stock count </p>
                        <small class="text-secondary py-1">This is the minimum stock count for all items. You can edit
                            later</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <input class="form-control" v-model="itemColumnImportOptions.minimumStock" />
                    </div>


                </div>
                <hr />
                <div class="text-center pb-2">
                    <a @click="processAdvanceInventoryImport" class="btn btn-primary my-2 px-3 py-2">Submit settings</a>
                </div>

            </b-modal>

      <!-- Import inventory dropzone modal -->
      <b-modal ref="importinventorymodal" id="modal-1" hide-footer>
        <h6 class="text-smanel-dark-purple py-2 text-center"> Import Inventory </h6>
        <vue-dropzone ref="myVueDropzone" id="dropzone" :options="dropzoneOptions_p_message" :useCustomSlot="true" @vdropzone-success="uploadSuccess" @vdropzone-error="uploadError" @vdropzone-removed-file="fileRemoved">
          <div class="dropzone-custom-content">
            <div class="text-center">
              <p class="text-secondary"><i class="fas fa-file-alt fa-2x"></i></p>
              <p><small class="dropzone-custom-title text-secondary py-2">Drag & Drop</small></p>
              <a class="btn btn-outline-secondary" href="#">Choose File</a>
              <p><small class="text-secondary">Max file size 2MB, only .xls or .xlsx allowed</small></p>
            </div>
          </div>
        </vue-dropzone>
        <div class="py-3 text-center"><p>Don't know where to start? we got you.</p><p class="text-smanel-dark-purple py-2"><a href="/files/DUKAKIT_Store_Manager.xlsx" download><i class="bi bi-download"></i><small> Here is a sample file download and use</small></a></p></div>
      </b-modal>

      <!-- Transfer single product modal (preserved) -->
      <b-modal ref="transfer-prod-modal" id="modal-1" hide-footer>
        <h6 class="text-smanel-dark-purple py-2 text-center">Transfer Stock Item</h6>
        <form @submit.prevent="transferStockItem">
          <div class="form-group">
            <label>Where do you want to transfer your stock?</label>
            <select class="form-control" v-model="transferForm.branch">
              <option value="null">Select Branch</option>
              <option v-for="branch in otherBranches" :key="branch.id" :value="branch.company ? branch.company.id : branch.id" v-if="branch.company">{{ branch.company.name }}</option>
            </select>
          </div>

          <div class="form-group">
            <label>Quantity to transfer</label>
            <input type="text" placeholder="eg. 5,20 etc" class="form-control" v-model="transferForm.qty" />
            <small>You can only transfer/move up to {{ selectedProduct.remaining_units }} {{ selectedProduct.measuring_unit }}</small>
          </div>

          <div class="form-group">
            <button type="submit" class="btn btn-block btn-primary">Submit and Transfer {{ selectedProduct.product }}</button>
          </div>
        </form>
      </b-modal>

      <!-- Bulk Transfer modal -->
      <b-modal ref="bulkTransferModal" id="bulk-transfer-modal" size="lg" hide-footer>
        <h5 class="text-center py-2">Bulk Transfer Selected Items</h5>
        <form @submit.prevent="submitBulkTransfer">
          <div class="form-row">
            <div class="form-group col-md-6">
              <label>Source Branch</label>
              <select class="form-control" v-model="bulkTransferForm.from_branch">
                <option value="">Select source branch</option>
                <option v-for="branch in otherBranches" :key="'from-'+(branch.company ? branch.company.id : branch.id)" :value="branch.company ? branch.company.id : branch.id">
                  {{ branch.company ? branch.company.name : branch.name }}
                </option>
              </select>
            </div>

            <div class="form-group col-md-6">
              <label>Destination Branch</label>
              <select class="form-control" v-model="bulkTransferForm.to_branch">
                <option value="">Select destination branch</option>
                <option v-for="branch in otherBranches" :key="'to-'+(branch.company ? branch.company.id : branch.id)" :value="branch.company ? branch.company.id : branch.id">
                  {{ branch.company ? branch.company.name : branch.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr><th>Product</th><th>Current Stock</th><th>Quantity to transfer</th></tr>
              </thead>
              <tbody>
                <tr v-for="(si, idx) in selectedItemsForTransfer" :key="si.id">
                  <td>{{ si.product }}</td>
                  <td>{{ si.remaining_units }}</td>
                  <td style="min-width:200px;">
                    <input
                      type="number"
                      class="form-control"
                      :class="{ 'is-invalid': si.transferError }"
                      v-model.number="si.transferQty"
                      :min="1"
                      :max="si.remaining_units"
                      @input="validateTransferQty(si)"
                    />
                    <div v-if="si.transferError" class="invalid-feedback">{{ si.transferError }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-2">
            <div><strong>Total Qty:</strong> {{ totalTransferQty }}</div>
            <div>
              <button class="btn btn-secondary mr-2" type="button" @click="$refs.bulkTransferModal.hide()">Cancel</button>
              <button class="btn btn-primary" type="submit" :disabled="!canSubmitBulkTransfer">Submit Bulk Transfer</button>
            </div>
          </div>
        </form>
      </b-modal>
      <!-- Bulk Issue modal -->
      <b-modal ref="bulkIssueItems" id="bulk-issue-modal" size="lg" hide-footer>
   <!-- Issue Items Modal -->


        <h5 class="modal-title" id="issueItemsLabel">Issue Items to Department</h5>


<form @submit.prevent="submitBulkIssue">
        <div class="form-group">
          <label for="receiver">Select User (Receiver)</label>
          <select v-model="issueForm.receiver_id" class="form-control">
            <option value="" disabled>Select a user</option>
            <option class="text-capitalize" v-for="user in departmentUsers" :key="user.id" :value="user.users.id">
              {{ (user.first_name)? user.first_name:user.users.name}}
            </option>
          </select>
        </div>

        <!-- Items List -->
        <h6 class="mt-3">Items to Issue</h6>
        <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr><th>Product</th><th>Current Stock</th><th>Quantity to transfer</th></tr>
              </thead>
              <tbody>
                <tr v-for="(si, idx) in selectedItemsForIssue" :key="si.id">
                  <td>{{ si.product }}</td>
                  <td>{{ si.remaining_units }}</td>
                  <td style="min-width:200px;">
                    <input
                      type="number"
                      class="form-control"
                      :class="{ 'is-invalid': si.transferError }"
                      v-model.number="si.transferQty"
                      :min="1"
                      :max="si.remaining_units"
                      @input="validateTransferQty(si)"
                    />
                    <div v-if="si.transferError" class="invalid-feedback">{{ si.transferError }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
        </div>


      <div class="modal-footer border-0">
        <button type="button" class="btn btn-light" data-dismiss="modal">Cancel</button>
        <button class="btn btn-primary" type="submit">
          Issue Items
        </button>
      </div>
      </form>



      </b-modal>

      <!-- Change category modal -->
      <b-modal ref="changeCatNameModal" hide-footer hide-header>
        <div class="text-center">
          <h3>Change Category Title</h3>
          <hr/>
        </div>
        <form @submit.prevent="changeCatNameSubmit">
          <div class="form-group">
            <input type="text" v-model="selectedCategoryToUpdate.text" class="form-control" :placeholder="selectedCategoryToUpdate.text" />
          </div>
          <div class="form-group">
            <button type="submit" class="btn btn-primary btn-block">Submit</button>
          </div>
        </form>
      </b-modal>

      <!-- All categories modal -->
      <b-modal id="modal-4" no-close-on-backdrop hide-footer>
        <h5 class="py-3 text-center">All Categories</h5>
        <table class="table">
          <thead><tr><th>#</th><th>Category</th><th>Action</th></tr></thead>
          <tbody>
            <tr v-for="(category, key) in itemCategories" :key="category.value">
              <td>{{ key + 1 }}</td>
              <td>{{ category.text }}</td>
              <td>
                <div class="dropdown">
                  <a href="#" role="button" data-toggle="dropdown"><i class="bi bi-three-dots-vertical"></i></a>
                  <div class="dropdown-menu">
                    <a class="dropdown-item" @click="changeCategoryName(category)" href="#">Rename</a>
                    <a class="dropdown-item text-danger" @click="deleteCategory(category)" href="#">Delete <p><small>Applies only to categories that have no items</small></p></a>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </b-modal>

      <!-- New supplier modal -->
      <b-modal ref="newSupplierModal" id="new-supplier-modal" hide-footer static>
        <div class="container text-center">
          <div class="text-centre"><h2 class="font-weight-bold">Add New Supplier</h2></div>
        </div>
        <div class="container py-3">
          <form @submit.prevent="createNewSupplier">
            <p>Company details</p>
            <div class="form-group"><input class="form-control border-bottom" v-model="supplierDetails.supplier.supplier" type="text" placeholder="Company Name" required/></div>
            <div class="form-row py-1">
              <div class="col-6"><input class="form-control border-bottom" v-model="supplierDetails.supplier.email" type="email" placeholder="Email"/></div>
              <div class="col-6"><input class="form-control border-bottom" v-model="supplierDetails.supplier.phone" type="text" placeholder="Phone Number"/></div>
            </div>
            <hr />
            <p>Company representative details</p>
            <div class="form-row py-1">
              <div class="col-6"><input class="form-control border-bottom" v-model="supplierDetails.supplier.supplier_representatives.name" type="text" placeholder="Representative "/></div>
              <div class="col-6"><input class="form-control border-bottom" v-model="supplierDetails.supplier.supplier_representatives.phone" type="text" placeholder="Phone"/></div>
            </div>
            <div class="form-group pt-3"><button class="btn btn-primary btn-block">Add Supplier</button></div>
          </form>
        </div>
      </b-modal>

      <!-- Error modal -->
      <b-modal ref="errorModal" hide-footer>
        <div class="container text-center">
          <h6 class="text-danger">There was an error</h6>
          <hr/>
          <p>{{ errorModalMessage }}</p>
          <a class="btn btn-outline-primary my-2 btn-block" href="/inventory">Retry</a>
        </div>
      </b-modal>

    </div>

    <div v-else class="text-center">
      <div class="spinner-border" role="status"><span class="sr-only">Loading Inventory...</span></div>
    </div>
  </div>
</template>

<script>
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapGetters } from "vuex";
import productCacheService from "../IndexDB/productCacheService";
import axios from "axios";

export default {
  name: "products",
  components: {
    vueDropzone: vue2Dropzone,
    DateRangePicker,
    vSelect
  },
  props: ["loadmodal"],
  data() {
    return {
      restockLogs: [],
      supplierOptions: [],
      selectedProduct: { product: {} },
      filters: { dateRange: { startDate: '', endDate: '' }, supplier_id: "", buying_price_change: "" },
      dateRange: { startDate: '', endDate: '' },
      autoApply: true,
      append_to_body: true,
      datepicker_opens: "right",
      defaultRanges: false,
      pageHasLoaded: false,
      products: "",
      otherBranches: [],
      errorModalMessage: "",
      todelete: "",
      stockCount: "",
      stockValue: "",
      uniqueStockCount: "",
      restockform: { quantity: "", product_id: "", adjustBuyingPrice: false, adjustSellingPrice: false, changeBuyingPriceImmediately: false, changeSellingPriceImmediately: false, newBuyingPrice: "", addSupplier: false, searchSupplier: "" },
      supplierDetails: { supplier: { email: "", phone: "", supplier: "", supplier_representatives: { name: "", email: "", phone: "" } }, searchTerm: "", searchTable: false, filteredSuppliers: [], selectedSupplier: {} },
      editprodform: { product: "", remaining_units: 0, wholesale_sp: 0, wholesale_ppu: 0, measuring_unit: "", sku: "", productcat_id: null, expiry_date: "", discount: 0, weight: "", color: "", tags: "", more_details: null, variations: [{ id: null, name: "", code: "", board_type: "room_only", price_per_night: null, price_per_adult: null, price_per_child: null, is_active: true, meta: {} }] },
      newproductcat: "",
      selectedCategoryToUpdate: {},
      items: [],
      perPageInventoryTable: 500,
      currentPageInventoryTable: 1,
      importitemcategory: "",
      fields: [
        { key: "product", sortable: true, label: "Item" },
        { key: "remaining_units", sortable: true, label: "Stock Count" },
        { key: "wholesale_ppu", sortable: true, label: "Buying Price" },
        { key: "ppu", sortable: true, label: "Selling Price" },
        { key: "wholesale_sp", sortable: true, label: "W/Selling Price" },
        { key: "stock_value", sortable: true, label: "Stock Value" }
      ],
      itemCategories: [],
      searchItems: "",
      show: true,
      queryselected: null,
      uploadItemErr: "",
      importitemcategory: null,
      dropzoneOptions_p_message: { url: "/api/import-inventory", addRemoveLinks: true, maxFiles: 1, headers: { "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr("content") } },
      dropzoneOptionsProductImages: { url: "/api/files", addRemoveLinks: true, maxFiles: 1, headers: { "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr("content") } },
      importInventoryHeaders: {},
      itemColumnImportOptions: { itemName: null, sku: null, buyingPrice: null, sellingPrice: null, remainingUnits: null, wholesaleSellingPrice: null, measuringUnit: null, fileName: "", minimumStock: 10, category: null },
      importSelectionErrors: {},
      selectedCategory: "",
      percentageMargins: 0,
      companySettings: "",
      estimatedSalesValue: "",
      transferForm: { branch: null, productId: "", qty: "" },
      linkedProductForm: { searchTerm: "", searchTable: false, filteredInventoryItems: [], product: {}, target_product_id: "", target_product: "", quantity_change: "", primary_product_id: "", hideSearchInput: false },

      // Bulk feature state
      selectedIds: [],
      selectAll: false,
      bulkTransferForm: { from_branch: "", to_branch: "" },

      // transfer items list (each entry will include transferQty and validation fields)
      transferItemsState: [],
    departmentUsers: [], // will be fetched from API
      availableItems: [],  // fetched from API
      issueForm: {
        receiver_id: '',
        items: []
      },
      reservation: {}, // current reservation
    };
  },
  computed: {
    ...mapGetters(["userDetails"]),
    isInventoryManager() {
      return this.userDetails && (this.userDetails.roles[0].name === "Admin" || this.hasPermission("can manage inventory"));
    },
    tableFields() {
      return [{ key: "selected", label: "", thStyle: { width: "40px" } }, ...this.fields];
    },
    filteredInventoryItems() {
      const q = this.searchItems ? this.searchItems.toLowerCase() : "";
      return this.items.filter(item => {
        const itemName = item.product ? item.product.toLowerCase() : "";
        const sku = item.sku ? item.sku.toLowerCase() : "";
        return itemName.includes(q) || sku.includes(q);
      });
    },
    rowsCompute() {
      return this.uniqueStockCount;
    },
    selectedCount() {
      return this.selectedIds.length;
    },
    selectAllComputed: {
      get() {
        return this.selectAll;
      },
      set(val) {
        this.selectAll = val;
      }
    },
    // Items that are currently selected represented with their data and transfer fields
    selectedItemsForTransfer() {
      return this.transferItemsState;
    },
    selectedItemsForIssue() {
      return this.transferItemsState;
    },
    totalTransferQty() {
      return this.transferItemsState.reduce((sum, it) => {
        const q = Number(it.transferQty) || 0;
        return sum + q;
      }, 0);
    },
    canSubmitBulkTransfer() {
      if (!this.bulkTransferForm.from_branch || !this.bulkTransferForm.to_branch) return false;
      if (this.transferItemsState.length === 0) return false;
      // all items must have valid qty and no transferError
      return this.transferItemsState.every(it => !it.transferError && Number(it.transferQty) > 0);
    }
  },
  watch: {
    "supplierDetails.searchTerm"(val) {
      if (val && val.length > 0) {
        this.supplierDetails.searchTable = true;
        this.searchSupplier();
      } else {
        this.supplierDetails.searchTable = false;
      }
    },
    "linkedProductForm.searchTerm"(val) {
      if (val && val.length > 0) {
        this.linkedProductForm.searchTable = true;
        this.searchInventoryItem();
      } else {
        this.linkedProductForm.searchTable = false;
      }
    },
    queryselected() {
      this.getProducts();
    },
    "itemColumnImportOptions.itemName"(val) {
      if (val === "null") {
        this.importSelectionErrors.itemName = "Item name required, make sure you have an item name/title column in your file";
      }
    },
    "itemColumnImportOptions.category"(val) {
      if (val === "null") {
        this.importSelectionErrors.category = "Category required, make sure you have a category column in your file";
      }
    },
    "itemColumnImportOptions.buyingPrice"(val) {
      if (val === "null") {
        this.importSelectionErrors.buyingPrice = "Make sure you have buying price column in your file";
      }
    },
    "itemColumnImportOptions.sellingPrice"(val) {
      if (val === "null") {
        this.importSelectionErrors.sellingPrice = "Make sure you have selling price column in your file";
      }
    },
    "editprodform.wholesale_ppu"(newVal) {
      if (!this.selectedProduct || typeof this.selectedProduct !== "object") this.selectedProduct = {};
      const cleanVal = parseFloat(newVal);
      if (!isNaN(cleanVal)) {
        this.selectedProduct.wholesale_ppu = cleanVal;
        this.calculatePercentageMargin(this.editprodform.ppu, cleanVal);
      } else {
        this.selectedProduct.wholesale_ppu = 0;
        this.editprodform.wholesale_ppu = 0;
      }
    },
    "editprodform.wholesale_sp"() {
      if (!this.selectedProduct || typeof this.selectedProduct !== "object") this.selectedProduct = {};
      if (isNaN(this.editprodform.wholesale_sp)) {
        this.editprodform.wholesale_sp = 0;
      }
    },
    "editprodform.ppu"() {
      if (!this.selectedProduct || typeof this.selectedProduct !== "object") this.selectedProduct = {};
      if (!isNaN(this.editprodform.ppu)) {
        this.selectedProduct.ppu = parseFloat(this.editprodform.ppu);
        this.calculatePercentageMargin(this.editprodform.ppu, this.editprodform.wholesale_ppu);
      } else {
        this.editprodform.ppu = 0;
      }
    }
  },
  mounted() {
    this.getProducts();
    this.getProductsCategories();
    this.loadImportModal();
    this.getCompanySettings();
  },
  methods: {
    /* -------------------------
       Issuing of stock items
       ------------------------- */

       async openIssueModal(reservation) {

       if (!this.isInventoryManager) return;
      if (this.selectedIds.length === 0) {
        this.$toasts.push({ type: "error", message: "No items selected for transfer." });
        return;
      }
      // prepare transferItemsState: fetch item details from items list
      this.transferItemsState = this.selectedIds.map(id => {
        const source = this.items.find(it => it.id === id) || { id, product: "Unknown", remaining_units: 0 };
        return {
          id: source.id,
          product: source.product,
          remaining_units: Number(source.remaining_units) || 0,
          transferQty: 1,
          transferError: null
        };
      });
      await this.fetchDepartmentUsers();
    //   await this.fetchAvailableItems();
      this.resetIssueForm();
      this.$refs.bulkIssueItems.show()
    //   $('#issueItemsModal').modal('show');
    },

    async fetchDepartmentUsers() {


                axios.get('/api/employees')
                    .then(result => {
                        this.departmentUsers = result.data;

                    }).catch(error => {
                        // console.log(error.response.data)
                    })
    },



    resetIssueForm() {
      this.issueForm.receiver_id = '';
      this.issueForm.items = [];
    },

    async submitBulkIssue(){
      // validate all
      let hasError = false;
      this.transferItemsState.forEach(it => {
        this.validateTransferQty(it);
        if (it.transferError) hasError = true;
      });
      if (hasError) {
        this.$toasts.push({ type: "error", message: "Please fix the quantity errors before submitting." });
        return;
      }

      if (this.transferItemsState.length === 0) {
        this.$toasts.push({ type: "error", message: "No items to Issue." });
        return;
      }

      // build payload
      const itemsPayload = this.transferItemsState.map(it => ({ id: it.id, quantity: Number(it.transferQty) }));
      const payload = {
        receiver_id: this.issueForm.receiver_id,

        items: itemsPayload
      };

      try {
        const res = await axios.post("/api/bulk-issue", payload);
        if (res && (res.status === 200 || res.status === 201)) {
          this.$toasts.push({ type: "success", message: res.data.message || "Issuing successful." });
          this.$refs.bulkIssueItems.hide();
          this.clearSelection();
          this.getProducts();
        } else {
          this.$toasts.push({ type: "error", message: res.data.message || "Issuing failed." });
        }
      } catch (err) {
        console.error("bulk transfer error", err);
        const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : "Error transferring items";
        this.$toasts.push({ type: "error", message: msg });
      }
    },

    /* -------------------------
       Utility & preserved methods
       ------------------------- */
    getCompanySettings() {
      this.companySettings = this.userDetails.company.companysettings;
    },
    hasPermission(permissionName) {
      return this.userDetails.permissions.some(permission => permission.name === permissionName);
    },
    formatDate(date, format) {
      return moment(date).format(format);
    },
    calculatePercentageMargin(ppu, wholesale_ppu) {
      if (!wholesale_ppu || wholesale_ppu === 0) return 0;
      return (((ppu - wholesale_ppu) / wholesale_ppu) * 100).toFixed(2);
    },
    downloadInventory() {
      axios.get("/api/download-inventory", { responseType: "blob" })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          const fileName = moment().format("HIs-M-Y");
          link.href = url;
          link.setAttribute("download", "Inventorty-" + fileName + ".xlsx");
          document.body.appendChild(link);
          link.click();
        })
        .catch(error => {
          console.error(error);
        });
    },

    /* -------------------------
       Selection & Bulk helpers
       ------------------------- */
    isSelected(id) {
      return this.selectedIds.includes(id);
    },

    toggleSelect(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) {
        this.selectedIds.push(id);
      } else {
        this.selectedIds.splice(idx, 1);
      }
      // update selectAll if needed
      this.selectAll = this.selectedIds.length === this.filteredInventoryItems.length && this.filteredInventoryItems.length > 0;
    },

    toggleSelectAll() {
      if (this.selectAll) {
        // deselect all
        this.selectedIds = [];
        this.selectAll = false;
      } else {
        // select all visible items
        this.selectedIds = this.filteredInventoryItems.map(i => i.id);
        this.selectAll = true;
      }
    },

    clearSelection() {
      this.selectedIds = [];
      this.selectAll = false;
      this.transferItemsState = [];
    },

    handleRowClicked(item, index, event) {
      // if clicked element is a checkbox input, ignore
      const t = event.target;
      if (t && (t.tagName === "INPUT" || t.type === "checkbox" || t.closest && t.closest('input[type="checkbox"]'))) {
        return;
      }
      this.rowSelected(item);
    },

    /* -------------------------
       Bulk Delete (POST to /api/bulk-delete)
       ------------------------- */
    confirmBulkDelete() {
      if (!this.isInventoryManager) return;
      if (this.selectedIds.length === 0) {
        this.$toasts.push({ type: "error", message: "No items selected." });
        return;
      }

     this.showMsgBoxTwo(this.selectedIds.length)



    },
  showMsgBoxTwo(count) {
        this.boxTwo = ''
        this.$bvModal.msgBoxOk(`Are you sure you want delete ${count} items?`, {
          title: 'Delete Items !',
          size: 'md',
          buttonSize: 'lg',
          okVariant: 'danger',
          headerClass: 'p-2 border-bottom-0',
          footerClass: 'p-2 border-top-0',
          centered: true
        })
          .then(value => {

            this.bulkDelete();
          })
          .catch(err => {
            this.$toasts.push()
          })
      },
    async bulkDelete() {
      try {
        const payload = { item_ids: this.selectedIds };
        const res = await axios.post("/api/bulk-delete", payload);
        if (res && (res.status === 200 || res.status === 201)) {
          this.$toasts.push({ type: "success", message: res.data.message || "Items deleted successfully." });
          // refresh
          this.clearSelection();
          this.getProducts();
        } else {
          this.$toasts.push({ type: "error", message: res.data.message || "Failed to delete items." });
        }
      } catch (err) {
        console.error("bulk delete error", err);
        const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : "Error deleting items";
        this.$toasts.push({ type: "error", message: msg });
      }
    },

    /* -------------------------
       Bulk Transfer flow
       - openBulkTransferEditor builds transferItemsState with per-item defaults
       ------------------------- */
    openBulkTransferEditor() {
      if (!this.isInventoryManager) return;
      if (this.selectedIds.length === 0) {
        this.$toasts.push({ type: "error", message: "No items selected for transfer." });
        return;
      }
      // prepare transferItemsState: fetch item details from items list
      this.transferItemsState = this.selectedIds.map(id => {
        const source = this.items.find(it => it.id === id) || { id, product: "Unknown", remaining_units: 0 };
        return {
          id: source.id,
          product: source.product,
          remaining_units: Number(source.remaining_units) || 0,
          transferQty: 1,
          transferError: null
        };
      });
      // fetch branches
      this.fetchOtherBranches();
      // reset bulk form
      this.bulkTransferForm = { from_branch: "", to_branch: "" };
      this.$nextTick(() => {
        this.$refs.bulkTransferModal.show();
      });
    },

    validateTransferQty(si) {
      si.transferError = null;
      const qty = Number(si.transferQty);
      if (!Number.isFinite(qty) || qty < 1) {
        si.transferError = "Quantity must be at least 1.";
      } else if (qty > si.remaining_units) {
        si.transferError = `Quantity cannot exceed available stock (${si.remaining_units}).`;
      } else {
        si.transferError = null;
      }
    },

    async submitBulkTransfer() {
      // validate all
      let hasError = false;
      this.transferItemsState.forEach(it => {
        this.validateTransferQty(it);
        if (it.transferError) hasError = true;
      });
      if (hasError) {
        this.$toasts.push({ type: "error", message: "Please fix the quantity errors before submitting." });
        return;
      }
      if (!this.bulkTransferForm.from_branch || !this.bulkTransferForm.to_branch) {
        this.$toasts.push({ type: "error", message: "Please select both source and destination branches." });
        return;
      }
      if (this.transferItemsState.length === 0) {
        this.$toasts.push({ type: "error", message: "No items to transfer." });
        return;
      }

      // build payload
      const itemsPayload = this.transferItemsState.map(it => ({ id: it.id, quantity: Number(it.transferQty) }));
      const payload = {
        source_branch: this.bulkTransferForm.from_branch,
        destination_branch: this.bulkTransferForm.to_branch,
        items: itemsPayload
      };

      try {
        const res = await axios.post("/api/bulk-transfer", payload);
        if (res && (res.status === 200 || res.status === 201)) {
          this.$toasts.push({ type: "success", message: res.data.message || "Bulk transfer successful." });
          this.$refs.bulkTransferModal.hide();
          this.clearSelection();
          this.getProducts();
        } else {
          this.$toasts.push({ type: "error", message: res.data.message || "Bulk transfer failed." });
        }
      } catch (err) {
        console.error("bulk transfer error", err);
        const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : "Error transferring items";
        this.$toasts.push({ type: "error", message: msg });
      }
    },

    /* -------------------------
       Branches fetch helper
       ------------------------- */
    async fetchOtherBranches() {
      try {
        const res = await axios.get("/api/other-branches");
        this.otherBranches = res.data || [];
      } catch (e) {
        console.error("Failed to fetch branches", e);
        this.otherBranches = [];
      }
    },

    /* -------------------------
       Preserved original methods
       ------------------------- */
    searchSupplier() {
      const searchTerm = this.supplierDetails.searchTerm.toLowerCase();
      this.supplierDetails.filteredSuppliers = this.supplierDetails.supplier.filter(supplier =>
        (supplier.supplier && supplier.supplier.toLowerCase().includes(searchTerm)) ||
        (supplier.email && supplier.email.toLowerCase().includes(searchTerm)) ||
        (supplier.phone && supplier.phone.includes(searchTerm))
      );
    },

    uploadSuccess(file, response) {
      this.$toasts.push({ type: "success", message: "File uploaded successfully" });
      this.importInventoryHeaders = response.headers;
      this.itemColumnImportOptions.fileName = response.file;
      this.$refs.importinventorymodal.hide();
      this.$refs.itemcolumnselect.show();
    },

    uploadImageSuccess(file, response) {
      this.$toasts.push({ type: "success", message: "File uploaded successfully" });
      this.editprodform.productImage = response.file;
    },

    uploadError(file, message) {
      this.$toasts.push({ type: "error", message: "There was an error uploading your file" });
    },

    fileRemoved() {},

    rowSelected(items) {
      if (this.isInventoryManager) {
        this.selectedProduct = items;
        this.percentageMargins = this.calculatePercentageMargin(items.ppu, items.wholesale_ppu);
        this.$refs.productmodal.show();
      }
    },

    async getProducts() {
      const urlParams = new URLSearchParams(window.location.search);
      try {
        const result = await axios.get("/api/products", {
          params: {
            search: this.searchItems,
            category_id: this.queryselected,
            lowStock: urlParams.has("low-stock") && urlParams.get("low-stock") === "true" ? 1 : 0,
            expiry: urlParams.has("expiring-soon") && urlParams.get("expiring-soon") === "true" ? 1 : 0
          }
        });
        if (result.status === 200) {
          this.items = result.data.items;
          this.stockValue = this.$root.convertCurrencyToLocal(result.data.totalStockValue);
          this.estimatedSalesValue = this.$root.convertCurrencyToLocal(result.data.totalSalesEstimate);
          this.stockCount = result.data.allItemsCount;
          this.uniqueStockCount = result.data.count;
          this.pageHasLoaded = true;
          // if select all was true previously, keep selecting visible ones
          if (this.selectAll) this.selectedIds = this.filteredInventoryItems.map(i => i.id);
        }
      } catch (e) {
        console.error(e);
        this.errorModalMessage = e.response && e.response.data ? e.response.data.message : "Error fetching products";
        this.$refs.errorModal.show();
      }
    },

    async getProductsCategories() {
      try {
        const result = await axios.get("/api/items-category");
        const catselect = [];
        $.each(result.data, function(key, value) {
          catselect.push({ text: value.name, value: value.id });
        });
        this.itemCategories = catselect;
      } catch (e) {
        console.error("Failed to get categories", e);
      }
    },

    addVariation() {
      this.editprodform.variations.push({ name: "", code: "", board_type: "room_only", price_per_night: null, price_per_adult: null, price_per_child: null, is_active: true, meta: {} });
    },

    removeVariation(index) {
      this.editprodform.variations.splice(index, 1);
    },

    async updateProduct() {
      try {
        const result = await axios.post("/api/p_edit", { ...this.editprodform });
        if (result.status === 200) {
          productCacheService.hotReloadLocalDB();
          this.$refs.editproductmodal.hide();
          this.$toasts.push({ type: "success", message: result.data.message });
          this.editprodform.product = "";
          this.editprodform.product_id = "";
          this.editprodform.ppu = "";
          this.editprodform.remaining_units = "";
          this.editprodform.measuring_unit = "";
          window.location.reload();
          this.selectedProduct = "";
        } else {
          this.$toasts.push({ type: "error", message: result.data.message });
        }
      } catch (e) {
        console.error("update error", e);
      }
    },

    async editProdModal(prod_id) {
      this.selectedProduct = "";
      try {
        const result = await axios.get("/api/p_details", { params: { product_id: prod_id } });
        if (result.status === 200) {
          this.selectedProduct = result.data;
          this.editprodform = result.data;
          this.editprodform.product_id = result.data.id;
          this.editprodform.productcat_id = result.data.category.id;
          this.editprodform.expiry_date = result.data.expiry_date;
          this.$refs.productmodal.hide();
          this.calculatePercentageMargin(this.selectedProduct.ppu, this.selectedProduct.wholesale_ppu);
          this.$refs.editproductmodal.show();
        }
      } catch (e) {
        console.error(e);
      }
    },
  restockLogsModalOpen(product) {
    // Save the product
    this.selectedProduct = product;

    // Reset filters if necessary
    this.filters = {
      dateRange: {
        startDate: '',
        endDate: '',
      },
      supplier_id: null,
      buying_price_change: null,
    };

    // Fetch logs immediately when modal opens
    this.fetchRestockLogs();

    // Show the modal
    if (this.$refs.restocklogsmodal) {
      this.$refs.restocklogsmodal.show();
    }
  },
async fetchRestockLogs() {
  // guard: need a selected product with id
  if (!this.selectedProduct || !this.selectedProduct.id) return;

  try {
    const { dateRange, supplier_id, buying_price_change } = this.filters || {};
    const params = {
      start_date: dateRange && dateRange.startDate ? this.formatDate(dateRange.startDate, 'YYYY-MM-DD') : '',
      end_date: dateRange && dateRange.endDate ? this.formatDate(dateRange.endDate, 'YYYY-MM-DD') : '',
      supplier_id: supplier_id || '',
      buying_price_change: buying_price_change || ''
    };

    const response = await axios.get(`/api/restock-logs/${this.selectedProduct.id}`, { params });

    // API shape in your original code: response.data.data.data
    this.restockLogs = (response && response.data && response.data.data && response.data.data.data) ? response.data.data.data : [];

    // Build supplierOptions array with unique suppliers (id + name)
    this.supplierOptions = this.restockLogs
      .map(log => log.supplier)
      .filter(s => s && s.id)
      .reduce((unique, supplier) => {
        if (!unique.some(u => u.id === supplier.id)) {
          unique.push({ id: supplier.id, name: supplier.supplier });
        }
        return unique;
      }, []);

    // show modal (hide product modal if needed)
    if (this.$refs && this.$refs.productmodal) {
      this.$refs.productmodal.hide && this.$refs.productmodal.hide();
    }
    if (this.$refs && this.$refs.restocklogsmodal) {
      this.$refs.restocklogsmodal.show && this.$refs.restocklogsmodal.show();
    }
  } catch (error) {
    console.error('Failed to fetch restock logs', error);
    // optional: show friendly error
    this.$toasts && this.$toasts.push && this.$toasts.push({ type: 'error', message: 'Failed to fetch restock logs' });
  }
},

    async restockProdModal(prod_id) {
      try {
        const result = await axios.get("/api/p_details", { params: { product_id: prod_id } });
        this.selectedProduct = result.data;
        this.editprodform.product_id = this.selectedProduct.id;
        this.$refs.productmodal.hide();
        this.getSuppliers();
        this.$bvModal.msgBoxConfirm('Do you know you can restock many items at once and also upload your receipt for later use. This will help you to track purchases', {
          title: 'Restock List Confirm',
          size: 'lg',
          buttonSize: 'sm',
          okVariant: 'primary',
          okTitle: 'I want to Restock More Items',
          cancelTitle: 'Restock only this item',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }).then(value => {
          if (value) {
            window.location.href = '/bulk-restock?restock-more';
          } else {
            this.$refs.productmodal_restock.show();

          }
        }).catch(() => {});
      } catch (e) {
        console.error(e);
      }
    },

    async transferProdModal(product) {
      this.selectedProduct = product;
      try {
        const result = await axios.get("/api/other-branches");
        this.otherBranches = result.data;
        this.$refs.productmodal.hide();
        this.$refs["transfer-prod-modal"].show();
      } catch (e) {
        console.error(e);
      }
    },

    async transferStockItem() {
      this.transferForm.productId = this.selectedProduct.id;
      try {
        const results = await axios.post("/api/transfer-items-to-another-branch", { ...this.transferForm });
        if (results.status === 200) {
          this.$refs["transfer-prod-modal"].hide();
          this.$toasts.push({ type: "success", message: "Items Transfered successfully" });
          window.location.reload();
        }
      } catch (e) {
        this.errorModalMessage = e.response && e.response.data ? e.response.data.message : "Error transferring";
        this.$refs.errorModal.show();
      }
    },

    async DeleteProd(prod_id) {
      try {
        const result = await axios.get("/api/p_delete", { params: { prod_id } });
        if (result.status === 200) {
          this.$toasts.push({ type: "success", message: result.data.message });
          window.location.reload();
        } else {
          this.$toasts.push({ type: "error", message: result.data.message });
        }
      } catch (e) {
        console.error(e);
      }
    },

    async Restock(prod_id) {
      try {
        const result = await axios.get("/api/p_details", { params: { product_id: prod_id } });
        this.selectedProduct = result.data;
        this.$refs.productmodal.show();
        this.restockform.product_id = this.selectedProduct.id;
      } catch (e) {
        console.error(e);
      }
    },

    async onSubmitRestock() {
      try {
        const result = await axios.post("/api/restock", { ...this.restockform, supplier_id: this.supplierDetails.selectedSupplier.id ? this.supplierDetails.selectedSupplier.id : "", product_id: this.selectedProduct.id });
        this.getProducts();
        this.selectedProduct = "";
        this.restockform.quantity = "";
        this.$refs["productmodal_restock"].hide();
        this.$toasts.push({ type: "success", message: result.data.message });
        window.location.reload();
      } catch (e) {
        console.error(e);
      }
    },

    async getSuppliers() {
      try {
        const result = await axios.get("/api/all-suppliers");
        this.supplierDetails.supplier = result.data;
        this.supplierDetails.supplier.supplier_representatives = {};
      } catch (e) {
        console.error(e);
      }
    },

    linkProductsModal() {
      this.$refs["productmodal"].hide();
      this.$refs.linkProductModal.show();
    },

    searchInventoryItem() {
      const searchTerm = this.linkedProductForm.searchTerm.toLowerCase();
      this.linkedProductForm.filteredInventoryItems = this.items.filter(product =>
        (product.product && product.product.toLowerCase().includes(searchTerm))
      );
    },

    loadImportModal() {
      if (this.loadmodal) {
        this.$refs["importinventorymodal"].show();
      }
    },

    linkProductSelected(item) {
      this.linkedProductForm.target_product_id = item.id;
      this.linkedProductForm.target_product = item.product;
      this.linkedProductForm.primary_product_id = this.selectedProduct.id;
      this.linkedProductForm.filteredInventoryItems = [];
      this.linkedProductForm.hideSearchInput = true;
    },

    async submitLinkProduct() {
      try {
        const results = await axios.post("/api/link-product", { ...this.linkedProductForm });
        this.$toasts.push({ type: results.data.type, message: results.data.message });
        window.location.reload();
      } catch (errors) {
        console.log(errors);
      }
    },

    createNewCategory() {
      if (this.newproductcat !== "") {
        axios.post('/api/manage-category/create/product', { productcat: this.newproductcat }).then(results => {
          this.getProductsCategories();
          this.$refs['createProductCatModal'].hide();
        }).catch(error => {
          axios.post('/api/report-error', {
            source: 'creating new category' + this.newproductcat,
            script: "products.vue",
            errormessage: error.response && error.response.data ? error.response.data.message : 'unknown',
            errorstatus: error.response && error.response.data ? error.response.data.status : 'unknown'
          }).then(() => {
            this.$toasts.push({ type: 'error', message: 'There was an error. Please retry' });
          });
        });
      }
    },

    createProductCatModal() {
      this.$refs['createProductCatModal'].show();
    },

    ImportInventoryModal() {
      this.$refs['importinventorymodal'].show();
    },

    processAdvanceInventoryImport() {
      if (this.itemColumnImportOptions.itemName === "null") {
        this.importSelectionErrors.itemName = "Item name required, make sure you have an item name/title column in your file";
      }
      if (this.itemColumnImportOptions.category === "null") {
        this.importSelectionErrors.category = "Category required, make sure you have a category column in your file";
      }
      if (this.itemColumnImportOptions.buyingPrice === "null") {
        this.importSelectionErrors.buyingPrice = "Make sure you have buying price column in your file";
      }
      if (this.itemColumnImportOptions.sellingPrice === "null") {
        this.importSelectionErrors.sellingPrice = "Make sure you have selling price column in your file";
      }

      axios.post('/api/process-inventory-import', { ...this.itemColumnImportOptions }).then(results => {
        this.$toasts.push({ type: results.data.type, message: results.data.message });
        window.location.replace('/products');
      }).catch(errors => {
        console.log(errors.response.data);
      });
    },

    ProcessUploadedFile() {
      this.uploadItemErr = '';
      const inventoryfile = document.getElementById('filename').value;
      if (this.importitemcategory === null) {
        this.uploadItemErr = "You need to select item category";
      }
      if (this.uploadItemErr === '') {
        axios.post('/api/importinventory', { inventoryfile: inventoryfile, category: this.importitemcategory }).then(results => {
          this.$toasts.push({ type: results.data.type, message: results.data.message });
          this.$refs['importinventorymodal'].hide();
        }).catch(errors => {
          console.log(errors.response.data);
        });
      }
    }
  }
};
</script>

<style scoped>
/* Minimal styles for validation */
.is-invalid {
  border-color: #e3342f;
}
.invalid-feedback {
  display: block;
}
/* small layout improvements for bulk bar */
.bg-light {
  background: #f8f9fa;
}
</style>
